/**
 * Normalizes facilitator-state `aggregates` and builds instructor answer-breakdown blocks
 * for quiz, drag-and-drop, and fill-in-the-blank knowledge checks.
 */

function num (v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function normalizeType (raw) {
  return String(raw?.type ?? raw?.kind ?? '').toLowerCase()
}

const DND_TYPES = new Set(['dnd', 'drag', 'drag-and-drop', 'drag_drop', 'matching'])
const FTB_TYPES = new Set(['ftb', 'fill', 'fill-in-the-blank', 'fill_in_blank'])

function choiceHasAggregateCount (choice) {
  if (choice == null || typeof choice !== 'object') {
    return false
  }
  return (
    choice.count != null ||
    choice.votes != null ||
    choice.n != null ||
    choice.responses != null ||
    choice.selected != null
  )
}

function markCorrectByLabel (labels, correctLabelOrIndex, correctIndices) {
  if (correctLabelOrIndex == null) {
    return
  }
  if (typeof correctLabelOrIndex === 'number' && Number.isFinite(correctLabelOrIndex)) {
    correctIndices.add(correctLabelOrIndex)
    return
  }
  const target = String(correctLabelOrIndex).trim().toLowerCase()
  if (!target) {
    return
  }
  labels.forEach((label, i) => {
    if (String(label || '').trim().toLowerCase() === target) {
      correctIndices.add(i)
    }
  })
}

function normalizeChoiceRow (row, fallbackLabels, correctLabelOrIndex) {
  if (!row || typeof row !== 'object') {
    return { labels: [], counts: [], correctIndices: new Set() }
  }

  const choices = row.choices ?? row.options
  const hasAggregateChoiceCounts =
    Array.isArray(choices) &&
    choices.length > 0 &&
    choices.some(choiceHasAggregateCount)
  if (hasAggregateChoiceCounts) {
    const labels = []
    const counts = []
    const correctIndices = new Set()
    choices.forEach((choice, i) => {
      if (choice == null) {
        return
      }
      if (typeof choice === 'object') {
        labels.push(String(choice.label ?? choice.text ?? fallbackLabels[i] ?? `Option ${i + 1}`).trim())
        counts.push(num(choice.count ?? choice.votes ?? choice.n ?? choice.responses ?? choice.selected))
        if (choice.correct === true) {
          correctIndices.add(i)
        }
      } else {
        labels.push(String(fallbackLabels[i] ?? `Option ${i + 1}`).trim())
        counts.push(num(choice))
      }
    })
    if (correctIndices.size === 0) {
      markCorrectByLabel(labels, correctLabelOrIndex, correctIndices)
    }
    return { labels, counts, correctIndices }
  }

  let counts = row.optionCounts ?? row.option_counts ?? row.counts
  if (!Array.isArray(counts) && Array.isArray(row.options)) {
    counts = row.options.map((o) => {
      if (o == null) {
        return 0
      }
      if (typeof o === 'object') {
        return num(o.count ?? o.votes ?? o.n ?? o.responses ?? o.selected)
      }
      return num(o)
    })
  }
  counts = Array.isArray(counts) ? counts.map((n) => num(n)) : []

  const labels = (row.choiceLabels ?? row.choice_labels ?? fallbackLabels ?? []).map((l) => String(l ?? '').trim())
  while (labels.length < counts.length) {
    labels.push(`Option ${labels.length + 1}`)
  }

  const correctIndices = new Set()
  const correctIndex = row.correctIndex ?? row.correct_index
  if (correctIndex != null && Number.isFinite(Number(correctIndex))) {
    correctIndices.add(num(correctIndex))
  } else {
    markCorrectByLabel(labels, correctLabelOrIndex, correctIndices)
  }

  return { labels, counts, correctIndices }
}

function barsFromChoiceRow (labels, counts, correctIndices) {
  const total = counts.reduce((sum, c) => sum + c, 0) || 1
  return labels.map((label, i) => ({
    label: label || `Option ${i + 1}`,
    count: counts[i] || 0,
    pct: Math.round(((counts[i] || 0) / total) * 100),
    isCorrect: correctIndices.has(i)
  }))
}

function normalizeAggregateItems (items) {
  if (!Array.isArray(items)) {
    if (items && typeof items === 'object') {
      return Object.keys(items)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => items[k])
    }
    return []
  }
  return items
}

function normalizeDndAggregates (raw) {
  const rows = normalizeAggregateItems(raw.rows ?? raw.pairs ?? raw.questions)
  return {
    type: 'dnd',
    rows: rows.map((row) => {
      const { labels, counts, correctIndices } = normalizeChoiceRow(row, [], null)
      return {
        ...row,
        prompt: row.prompt ?? row.left ?? row.label ?? null,
        correctAnswer: row.correctAnswer ?? row.correct ?? row.right ?? null,
        choiceLabels: labels,
        optionCounts: counts,
        correctIndex: correctIndices.size === 1 ? [...correctIndices][0] : row.correctIndex ?? row.correct_index ?? null
      }
    })
  }
}

function normalizeFtbAggregates (raw) {
  const blanks = normalizeAggregateItems(raw.blanks ?? raw.questions)
  return {
    type: 'ftb',
    blanks: blanks.map((row, blankIndex) => {
      const { labels, counts, correctIndices } = normalizeChoiceRow(row, [], null)
      return {
        ...row,
        blankIndex: row.blankIndex ?? row.blank_index ?? blankIndex,
        expected: row.expected ?? row.correct ?? row.correctAnswer ?? null,
        choiceLabels: labels,
        optionCounts: counts,
        correctIndex: correctIndices.size === 1 ? [...correctIndices][0] : row.correctIndex ?? row.correct_index ?? null
      }
    })
  }
}

/**
 * Normalizes facilitator-state `aggregates` for quiz, DnD, and FTB payloads.
 */
export function normalizeGroupWatchFacilitatorAggregates (raw) {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null
  }

  const typeStr = normalizeType(raw)
  const hasRows = Array.isArray(raw.rows ?? raw.pairs)
  const hasBlanks = Array.isArray(raw.blanks)

  if (DND_TYPES.has(typeStr) || (hasRows && !Array.isArray(raw.questions) && !hasBlanks)) {
    return normalizeDndAggregates(raw)
  }
  if (FTB_TYPES.has(typeStr) || (hasBlanks && !hasRows)) {
    return normalizeFtbAggregates(raw)
  }

  let questions = raw.questions
  if (!Array.isArray(questions) && raw.quiz && typeof raw.quiz === 'object') {
    questions = raw.quiz.questions
  }
  if (!Array.isArray(questions) && questions && typeof questions === 'object') {
    questions = Object.keys(questions)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => questions[k])
  }
  if (!Array.isArray(questions)) {
    const flat = raw.optionCounts ?? raw.option_counts
    if (Array.isArray(flat)) {
      questions = [{ optionCounts: flat }]
    } else {
      return null
    }
  }

  const normalizedQuestions = questions.map((q) => {
    if (!q || typeof q !== 'object') {
      return { optionCounts: [] }
    }
    let counts = q.optionCounts ?? q.option_counts
    if (!Array.isArray(counts) && Array.isArray(q.counts)) {
      counts = q.counts
    }
    if (!Array.isArray(counts) && Array.isArray(q.options)) {
      counts = q.options.map((o) => {
        if (o == null) {
          return 0
        }
        if (typeof o === 'object') {
          return num(o.count ?? o.votes ?? o.n ?? o.responses ?? o.selected)
        }
        return num(o)
      })
    }
    if (!Array.isArray(counts)) {
      counts = []
    }
    return {
      ...q,
      optionCounts: counts.map((n) => num(n))
    }
  })

  const explicitNonQuizAggTypes = new Set([
    ...DND_TYPES,
    ...FTB_TYPES,
    'flashcard',
    'flashcards',
    'sort'
  ])
  const looksLikeQuizAggregates =
    normalizedQuestions.length > 0 &&
    normalizedQuestions.every((q) => Array.isArray(q.optionCounts))
  let resolvedType = typeStr === 'quiz' || typeStr === '' ? 'quiz' : typeStr
  if (looksLikeQuizAggregates && !explicitNonQuizAggTypes.has(typeStr)) {
    resolvedType = 'quiz'
  }

  return {
    type: resolvedType,
    questions: normalizedQuestions
  }
}

export function getDndPairsFromKnowledgeCheck (kc) {
  const pairs = kc?.pairs
  if (!Array.isArray(pairs)) {
    return []
  }
  return pairs
    .filter((p) => p && p.left && p.right)
    .map((p) => ({ left: String(p.left), right: String(p.right) }))
}

export function getDndPoolLabels (pairs) {
  const seen = new Set()
  const labels = []
  for (const pair of pairs) {
    const text = String(pair.right || '').trim()
    const key = text.toLowerCase()
    if (text && !seen.has(key)) {
      seen.add(key)
      labels.push(text)
    }
  }
  return labels
}

export function getFtbBlankMetaFromKnowledgeCheck (kc) {
  const statement = String(kc?.statement || '')
  const pool = Array.isArray(kc?.pool) ? kc.pool : []
  if (!statement.includes('___') || !pool.length) {
    return { blanks: [], choiceLabels: [], parts: [] }
  }
  const parts = statement.split('___')
  const blankCount = parts.length - 1
  const blanks = []
  for (let b = 0; b < blankCount; b++) {
    const poolEntry = pool.find((p) => p && p.correctForBlank === b + 1) || pool[b] || null
    const expected =
      poolEntry && poolEntry.text != null
        ? String(poolEntry.text)
        : pool[b] && pool[b].text != null
          ? String(pool[b].text)
          : ''
    blanks.push({ expected, blankIndex: b })
  }
  return {
    blanks,
    choiceLabels: blanks.map((row) => row.expected).filter(Boolean),
    parts
  }
}

/**
 * Builds answer-breakdown blocks for the instructor "Answers" tab.
 * @returns {Array<{ blockIndex: number, blockTitle: string, bars: Array<{ label: string, count: number, pct: number, isCorrect: boolean }> }>}
 */
export function buildInteractiveAnswerBreakdownBlocks (aggregates, knowledgeCheck) {
  if (!aggregates || typeof aggregates !== 'object' || !knowledgeCheck) {
    return []
  }

  const kcType = String(knowledgeCheck.type || '').toLowerCase()
  const aggType = String(aggregates.type || kcType || 'quiz').toLowerCase()

  if (aggType === 'dnd' || (kcType === 'dnd' && Array.isArray(aggregates.rows))) {
    const pairs = getDndPairsFromKnowledgeCheck(knowledgeCheck)
    const poolLabels = getDndPoolLabels(pairs)
    const aggRows = aggregates.rows ?? aggregates.questions ?? []
    if (!Array.isArray(aggRows) || !aggRows.length) {
      return []
    }
    return aggRows.map((row, rowIndex) => {
      const pair = pairs[rowIndex] || {}
      const prompt = String(row.prompt ?? row.left ?? pair.left ?? `Match ${rowIndex + 1}`).trim()
      const correctLabel = String(row.correctAnswer ?? row.correct ?? row.right ?? pair.right ?? '').trim()
      const fallbackLabels = (row.choiceLabels ?? row.choice_labels ?? poolLabels).map(String)
      const { labels, counts, correctIndices } = normalizeChoiceRow(row, fallbackLabels, correctLabel)
      return {
        blockIndex: rowIndex,
        blockTitle: prompt,
        bars: barsFromChoiceRow(labels, counts, correctIndices)
      }
    })
  }

  if (aggType === 'ftb' || (kcType === 'ftb' && Array.isArray(aggregates.blanks))) {
    const { blanks, choiceLabels } = getFtbBlankMetaFromKnowledgeCheck(knowledgeCheck)
    const aggBlanks = aggregates.blanks ?? aggregates.questions ?? []
    if (!Array.isArray(aggBlanks) || !aggBlanks.length) {
      return []
    }
    return aggBlanks.map((row, blankIndex) => {
      const blankMeta = blanks[blankIndex] || {}
      const expected = String(row.expected ?? row.correct ?? row.correctAnswer ?? blankMeta.expected ?? '').trim()
      const fallbackLabels = (row.choiceLabels ?? row.choice_labels ?? choiceLabels).map(String)
      const { labels, counts, correctIndices } = normalizeChoiceRow(row, fallbackLabels, expected)
      return {
        blockIndex: blankIndex,
        blockTitle: `Blank ${blankIndex + 1}`,
        bars: barsFromChoiceRow(labels, counts, correctIndices)
      }
    })
  }

  const aggOk =
    Array.isArray(aggregates.questions) &&
    aggregates.questions.length > 0 &&
    (aggType === 'quiz' ||
      (Array.isArray(knowledgeCheck.questions) &&
        knowledgeCheck.questions.length > 0 &&
        !['dnd', 'ftb', 'flashcard'].includes(kcType)))
  if (!aggOk) {
    return []
  }

  const questions = Array.isArray(knowledgeCheck.questions) ? knowledgeCheck.questions : []
  return aggregates.questions.map((row, questionIndex) => {
    const counts = row.optionCounts || row.option_counts || []
    const options = questionIndex < questions.length && Array.isArray(questions[questionIndex].options)
      ? questions[questionIndex].options
      : []
    const fallbackLabels = options.map((o) => (o && o.text != null ? String(o.text) : ''))
    const { labels, counts: normalizedCounts, correctIndices } = normalizeChoiceRow(
      { optionCounts: counts, choiceLabels: fallbackLabels },
      fallbackLabels,
      null
    )
    options.forEach((option, optionIndex) => {
      if (option && option.correct === true) {
        correctIndices.add(optionIndex)
      }
    })
    return {
      blockIndex: questionIndex,
      blockTitle: questions[questionIndex] && questions[questionIndex].question
        ? questions[questionIndex].question
        : `Question ${questionIndex + 1}`,
      bars: barsFromChoiceRow(labels, normalizedCounts, correctIndices)
    }
  })
}
